import React, { useEffect, useState } from 'react';
import { useEdgeLinkStatus } from '@/lib/api/edgelink';
import { EdgeLinkStatus } from '@/lib/api/edgelink/types';
import { Button } from '@/components/ui/button';
import TunnelStatusCard from '../details/TunnelStatusCard';
import LogViewer from '../components/LogViewer';
import { ArrowLeft, Check, SkipForward } from 'lucide-react';
import { useEdgeLinkLogs } from '@/hooks/useEdgeLinkLogs';

interface VerificationStepProps {
  bridgeId: string;
  routeId: string;
  onComplete: (routeId: string) => void;
  onSkip: () => void;
  onBack: () => void;
}

export default function VerificationStep({
  bridgeId,
  routeId,
  onComplete,
  onSkip,
  onBack,
}: VerificationStepProps) {
  const [hasAutoProgressed, setHasAutoProgressed] = useState(false);

  const { data: statusData, isLoading: isStatusLoading } = useEdgeLinkStatus(bridgeId, true);

  // Live log streaming
  const { logs, isConnected: logsConnected } = useEdgeLinkLogs({
    bridgeId,
    enabled: true,
  });

  const isHealthy = statusData?.status === EdgeLinkStatus.HEALTHY;

  // Auto-enable complete when status becomes healthy (only once)
  useEffect(() => {
    if (isHealthy && !hasAutoProgressed) {
      setHasAutoProgressed(true);
    }
  }, [isHealthy, hasAutoProgressed]);

  const handleComplete = () => {
    onComplete(routeId);
  };

  return (
    <div className="flex flex-col">
      <div className="p-6 space-y-6">
        {/* Status description */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Connection Status</h3>
          <p className="text-xs text-muted-foreground">
            {isHealthy
              ? 'Your EdgeLink bridge is connected and ready to use.'
              : 'Waiting for your EdgeLink bridge to connect. Run the command from the previous step on your server.'}
          </p>
        </div>

        {/* Tunnel Status Card */}
        <TunnelStatusCard status={statusData} isLoading={isStatusLoading} />

        {/* Live logs */}
        {logs.length > 0 && (
          <LogViewer logs={logs} isConnected={logsConnected} compact maxHeight="150px" />
        )}

        {/* Help text when not connected */}
        {!isHealthy && !isStatusLoading && (
          <div className="p-4 rounded-lg bg-muted/10 border border-[#2E3835]">
            <p className="text-sm font-medium mb-2">Not seeing a connection?</p>
            <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
              <li>Make sure you've run the connection command on your server</li>
              <li>Check that your server has internet access</li>
              <li>Verify there are no firewall rules blocking outbound connections</li>
              <li>You can skip this step and verify the connection later</li>
            </ul>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-[#2E3835] bg-card2/50">
        <Button type="button" variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center gap-3">
          {!isHealthy && (
            <Button type="button" variant="outline" onClick={onSkip}>
              <SkipForward className="h-4 w-4 mr-2" />
              Skip for now
            </Button>
          )}
          <Button
            type="button"
            onClick={handleComplete}
            disabled={!isHealthy}
          >
            <Check className="h-4 w-4 mr-2" />
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
